"use client";

import * as React from "react";
import { LucideIcon, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { IconBadge, IconCircle, StatusDot } from "./icon-badge";

// ============================================================================
// ListItem - Versatile list item component for various use cases
// ============================================================================

export interface ListItemProps {
  /** Primary text */
  title: string;
  /** Secondary text */
  subtitle?: string;
  /** Third line of text */
  description?: string;
  /** Left side content - icon, avatar, or custom */
  leading?: {
    type: "icon" | "avatar" | "iconCircle" | "custom";
    icon?: LucideIcon;
    iconVariant?: "default" | "emerald" | "blue" | "amber" | "purple" | "rose";
    avatarSrc?: string;
    avatarFallback?: string;
    status?: "online" | "offline" | "away";
    custom?: React.ReactNode;
  };
  /** Right side content */
  trailing?: {
    type: "arrow" | "badge" | "text" | "custom";
    badgeText?: string;
    badgeVariant?: "default" | "secondary" | "outline";
    text?: string;
    subtext?: string;
    custom?: React.ReactNode;
  };
  /** Click handler */
  onClick?: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Visual style */
  variant?: "default" | "bordered" | "card";
  /** Size */
  size?: "sm" | "md" | "lg";
  /** Additional className */
  className?: string;
}

const variantStyles = {
  default: "hover:bg-muted/50",
  bordered: "border rounded-lg hover:bg-muted/50 hover:border-emerald-200 dark:hover:border-emerald-800",
  card: "border rounded-xl bg-card hover:shadow-md hover:-translate-y-0.5",
};

const sizeStyles = {
  sm: "p-2 gap-2",
  md: "p-3 gap-3",
  lg: "p-4 gap-4",
};

export function ListItem({
  title,
  subtitle,
  description,
  leading,
  trailing,
  onClick,
  disabled = false,
  variant = "bordered",
  size = "md",
  className,
}: ListItemProps) {
  const Comp = onClick ? "button" : "div";

  return (
    <Comp
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full flex items-center text-left transition-all duration-200",
        variantStyles[variant],
        sizeStyles[size],
        onClick && "cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
        disabled && "opacity-50 cursor-not-allowed",
        "group",
        className
      )}
    >
      {/* Leading content */}
      {leading && (
        <div className="flex-shrink-0 relative">
          {leading.type === "icon" && leading.icon && (
            <IconBadge 
              icon={leading.icon} 
              variant={leading.iconVariant}
              size={size === "lg" ? "lg" : "md"}
            />
          )}
          {leading.type === "iconCircle" && leading.icon && (
            <IconCircle 
              icon={leading.icon} 
              variant={leading.iconVariant}
              size={size === "lg" ? "lg" : "md"}
            />
          )}
          {leading.type === "avatar" && (
            <div className="relative">
              <Avatar className={cn(
                size === "sm" && "w-8 h-8",
                size === "md" && "w-10 h-10",
                size === "lg" && "w-12 h-12"
              )}>
                <AvatarImage src={leading.avatarSrc} />
                <AvatarFallback>{leading.avatarFallback || "?"}</AvatarFallback>
              </Avatar>
              {leading.status && (
                <div className="absolute -bottom-0.5 -right-0.5">
                  <StatusDot status={leading.status} size="sm" />
                </div>
              )}
            </div>
          )}
          {leading.type === "custom" && leading.custom}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          "font-medium line-clamp-1",
          size === "sm" && "text-sm",
          size === "md" && "text-sm",
          size === "lg" && "text-base"
        )}>
          {title}
        </p>
        {subtitle && (
          <p className={cn(
            "text-muted-foreground line-clamp-1",
            size === "sm" && "text-xs",
            size === "md" && "text-xs",
            size === "lg" && "text-sm"
          )}>
            {subtitle}
          </p>
        )}
        {description && (
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
            {description}
          </p>
        )}
      </div>

      {/* Trailing content */}
      {trailing && (
        <div className="flex-shrink-0 flex items-center gap-2">
          {trailing.type === "badge" && trailing.badgeText && (
            <Badge variant={trailing.badgeVariant || "secondary"}>
              {trailing.badgeText}
            </Badge>
          )}
          {trailing.type === "text" && (
            <div className="text-right">
              {trailing.text && (
                <p className="text-sm font-medium">{trailing.text}</p>
              )}
              {trailing.subtext && (
                <p className="text-xs text-muted-foreground">{trailing.subtext}</p>
              )}
            </div>
          )}
          {trailing.type === "arrow" && (
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
          )}
          {trailing.type === "custom" && trailing.custom}
        </div>
      )}
    </Comp>
  );
}

// ============================================================================
// ListGroup - Container for list items with optional header
// ============================================================================

export interface ListGroupProps {
  /** Optional header */
  header?: {
    title: string;
    action?: React.ReactNode;
  };
  /** List items */
  children: React.ReactNode;
  /** Gap between items */
  gap?: "none" | "sm" | "md";
  /** Additional className */
  className?: string;
}

export function ListGroup({
  header,
  children,
  gap = "sm",
  className,
}: ListGroupProps) {
  const gapStyles = {
    none: "space-y-0",
    sm: "space-y-2",
    md: "space-y-3",
  };

  return (
    <div className={cn("space-y-3", className)}>
      {header && (
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            {header.title}
          </h4>
          {header.action}
        </div>
      )}
      <div className={gapStyles[gap]}>
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// EmptyState - Placeholder for empty lists
// ============================================================================



